<template>
  <div class="flex items-start justify-start px-4 font-poppins">
    <div class="w-full  p-6 space-y-6 transition-all duration-300">

      <!-- Header -->
      <div class="">
        <p class="text-sm text-gray-500 mt-1">
          Code sent to {{ phoneNumber }}
        </p>
      </div>

      <!-- Error -->
      <transition name="fade">
        <div
          v-if="errorMessage"
          class="bg-red-50 text-red-600 text-sm p-3 rounded-xl animate-shake"
        >
          {{ errorMessage }}
        </div>
      </transition>

      <!-- Success -->
      <transition name="fade">
        <div
          v-if="successMessage"
          class="bg-green-50 text-green-600 text-sm p-3 rounded-xl"
        >
          {{ successMessage }}
        </div>
      </transition>

      <!-- OTP Input -->
      <input
        v-model="otp"
        type="text"
        maxlength="8"
        inputmode="numeric"
        placeholder="••••••"
        class="w-full text-center text-xl tracking-[0.5em] py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
        :class="{ 'border-red-400 ring-red-400': errorMessage }"
      />

      <!-- Countdown -->
      <div class="text-center text-sm text-gray-500">
        <span v-if="timeLeft > 0">
          Resend available in
          <span class="font-medium text-gray-700">
            {{ timeLeft }}s
          </span>
        </span>
        <span v-if="timeLeft <= 0 && timerStarted" class="text-green-600 font-medium animate-pulse">
          You can resend the code now
        </span>
      </div>

      <!-- Buttons -->
      <div class="flex gap-3">
        <button
          @click="verifyOtp"
          :disabled="otp.length < 6 || loading || newCode"
          class="flex-1 bg-blue-600 text-white py-2.5 rounded-xl font-medium hover:bg-blue-700 transition disabled:opacity-50 active:scale-95"
        >
          <span v-if="loading">Verifying...</span>
          <span v-else>Verify</span>
        </button>

        <button
          @click="resendOtp"
          :disabled="timeLeft > 0 || loading"
          class="flex-1 bg-gray-200 text-gray-700 py-2.5 rounded-xl font-medium hover:bg-gray-300 transition disabled:opacity-50 active:scale-95"
        >
          Resend
        </button>
      </div>

    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { serviceContainer } from '@/inversify';
import type { OtpClientInterface } from '@/services/integration/clients/otpClientInterface';
import { ApiException } from '@/services/integration/errors/ApiException';
import store from '@/store';
import { HTTPError } from 'ky';

const props = defineProps<{
  phoneNumber?: string;
  purpose?: string;
  active: boolean;
}>();

const emit = defineEmits<{(e: 'verified', data: unknown): void;
}>();

const otpClient = serviceContainer.get<OtpClientInterface>('OtpClient');

const otp = ref('');
const timeLeft = ref(0);
const loading = ref(false);
const errorMessage = ref('');
const successMessage = ref('');
const hasSent = ref(false);
const newCode = ref(false);
const timerStarted = ref(false);

let timer: ReturnType<typeof setInterval> | null = null;

/* ---------------------------
   TIMER
---------------------------- */
const startTimer = (seconds: number) => {
  timeLeft.value = seconds;

  if (timer) clearInterval(timer);

  timer = setInterval(() => {
    if (timeLeft.value > 0) {
      timeLeft.value--;
    } else if (timer) {
      clearInterval(timer);
    }
  }, 1000);
};

const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';

  // Remove spaces and common separators
  const cleaned = phone.replace(/\s|-/g, '');

  // Convert Australian format (04xx → +614xx)
  if (cleaned.startsWith('04')) {
    return `+61${cleaned.substring(1)}`;
  }

  // If already in E.164 format
  if (cleaned.startsWith('+')) {
    return cleaned;
  }

  // Default: add + prefix
  return `+${cleaned}`;
};

/* ---------------------------
   SEND OTP
---------------------------- */
const sendOtp = async () => {
  loading.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  try {
    const data = await otpClient.send(
      formatPhoneNumber(props.phoneNumber),
      props.purpose,
      store.state.cartId,
      'Nexee'
    );

    timerStarted.value = true;
    startTimer(data.expires_in_seconds);
    successMessage.value = 'Verification code sent';
  } catch (error) {
    handleError(error);
  } finally {
    loading.value = false;
  }
};

/* ---------------------------
   VERIFY OTP
---------------------------- */
const verifyOtp = async () => {
  loading.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  try {
    const data = await otpClient.verify(
      formatPhoneNumber(props.phoneNumber),
      otp.value,
      props.purpose
    );

    successMessage.value = 'Phone number verified';
    emit('verified', data);
  } catch (error) {
    handleError(error);
  } finally {
    loading.value = false;
  }
};

/* ---------------------------
   RESEND
---------------------------- */
const resendOtp = async () => {
  newCode.value = false;
  await sendOtp();
};

/* ---------------------------
   ERROR HANDLER
---------------------------- */
const handleError = async (err: unknown) => {
  console.log('OTP error:', err);

  if (err instanceof HTTPError) {
    const errorData = await err.response
      .json()
      .catch(() => null);

    otp.value = '';
    errorMessage.value = errorData?.error?.message;
    newCode.value = errorData?.error?.requires_new_code;

    throw new ApiException({
      message: errorData?.error?.message ?? err.message,
      code: errorData?.error?.code,
      retryable: errorData?.error?.retryable,
      requiresNewCode: errorData?.error?.requires_new_code,
      status: err.response.status,
      details: errorData
    });
  }

  errorMessage.value = 'Unexpected error occurred';

  throw err instanceof Error ? err : new Error('Unknown error');
};

/* ---------------------------
   WATCH ACTIVE STEP
---------------------------- */
watch(
  () => props.active,
  (isActive) => {
    if (isActive && !hasSent.value) {
      hasSent.value = true;
      sendOtp();
    }
  },
  { immediate: true }
);

/* ---------------------------
   RESET IF PHONE CHANGES
---------------------------- */
watch(
  () => props.phoneNumber,
  () => {
    hasSent.value = false;
  }
);
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-4px); }
  40% { transform: translateX(4px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(4px); }
}
.animate-shake {
  animation: shake 0.3s ease;
}
</style>
